/**
 * `email` — inline input with monday's `@`-shaped validation
 * (`discovery/ui-reference/editors/email/capture.json`: an **in-cell** editor 139 × 35,
 * 14px/400, no popover; README: *"Inline input with an `@`-shaped validation; invalid text is
 * refused on commit"*).
 *
 * The cell renders the **label**, which is `value.text` and equals the address when the user
 * typed none — the same two shapes `toText` renders.
 */
import { getColumnType, type EmailSettings, type EmailValue } from '@elizmonday/shared';
import { InlineTextEditor } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { EmailSettingsDialog } from './SettingsDialog';

const shared = getColumnType('email');

export const emailColumnUi = defineColumnUi<EmailValue, EmailSettings>({
  type: 'email',
  icon: 'email',
  gallery: { group: 'essentials', keywords: ['email', 'mail', 'contact', 'address'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value }) => {
    if (value === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <a
        className="ez-cell__link"
        href={`mailto:${value.email}`}
        title={value.email}
        onClick={(event) => event.stopPropagation()}
      >
        {value.text ?? value.email}
      </a>
    );
  },

  Editor: ({ value, initialInput, autoFocus, onCommit, onCancel, onNavigate }) => (
    <InlineTextEditor
      initial={value?.email ?? ''}
      initialInput={initialInput}
      autoFocus={autoFocus}
      ariaLabel="Email"
      placeholder="name@example.com"
      onCommit={(next) => {
        const trimmed = next.trim();
        if (trimmed === '') {
          onCommit(null);
          return;
        }
        // Refused on commit, as monday does: an invalid address leaves the cell unchanged.
        if (!/^[^\s@]+@[^\s@]+$/.test(trimmed)) {
          onCancel();
          return;
        }
        onCommit({ email: trimmed, text: value?.text === value?.email ? trimmed : (value?.text ?? trimmed) });
      }}
      onCancel={onCancel}
      onNavigate={onNavigate}
    />
  ),

  SettingsDialog: EmailSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const separator = trimmed.indexOf(' - ');
    const raw = separator > 0
      ? { text: trimmed.slice(0, separator), email: trimmed.slice(separator + 3) }
      : { email: trimmed };
    return shared.parseValue(raw, settings) as EmailValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('email'),
});
